import * as React from "react";
import {
  Button,
  Popover,
  OverlayToaster,
  Position,
  type OverlayToaster as OverlayToasterType,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ApiAdmin from "../../../api/admin";
import { FolderAdminEditFolderDialog } from "./FolderAdminEditFolderDialog";
import { DEACTIVATED_FOLDERS_QUERY_KEY } from "./folderAdminQueryKeys";

interface FolderAdminReactivatePopoverProps {
  onReactivated?: () => void;
}

export function FolderAdminReactivatePopover(
  props: FolderAdminReactivatePopoverProps
) {
  const { onReactivated } = props;
  const toaster = React.useRef<OverlayToasterType>(null);
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = React.useState(false);
  const [editFolder, setEditFolder] = React.useState<{
    id: number;
    label: string;
  } | null>(null);

  const deactivatedFoldersQuery = useQuery({
    queryKey: DEACTIVATED_FOLDERS_QUERY_KEY,
    queryFn: () => ApiAdmin.getDeactivatedFoldersForFolderAdmin(),
  });

  const reactivateFolderMutation = useMutation({
    mutationFn: async ({ folderId }: { folderId: number }) => {
      return ApiAdmin.reactivateFolderForFolderAdmin(folderId);
    },
    onError: (err: any) => {
      const baseMessage =
        err?.message || "Failed to reactivate folder. Please try again.";
      const message = baseMessage.includes("already exists")
        ? `${baseMessage} Rename it first.`
        : baseMessage;
      toaster.current?.show({
        message,
        intent: "danger",
        timeout: 5000,
      });
    },
    onSuccess: async () => {
      toaster.current?.show({
        message: "Folder reactivated successfully.",
        intent: "success",
        timeout: 3000,
      });
      await queryClient.invalidateQueries({
        queryKey: DEACTIVATED_FOLDERS_QUERY_KEY,
      });
      onReactivated?.();
    },
  });

  const folders = deactivatedFoldersQuery.data?.folders ?? [];

  if (folders.length === 0) {
    return null;
  }

  return (
    <>
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
      <Popover
        isOpen={isOpen}
        onInteraction={setIsOpen}
        placement="bottom-end"
        content={
          <div
            css={css`
              padding: 0.75rem;
              min-width: 240px;
              max-width: 320px;
            `}
          >
            <div
              css={css`
                font-weight: 600;
                margin-bottom: 0.5rem;
              `}
            >
              Deactivated folders
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
              `}
            >
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.5rem;
                  `}
                >
                  <span
                    css={css`
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `}
                  >
                    {folder.label}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      gap: 0.25rem;
                      flex-shrink: 0;
                    `}
                  >
                    <Button
                      small
                      icon="edit"
                      aria-label={`Edit ${folder.label}`}
                      disabled={reactivateFolderMutation.isPending}
                      onClick={() =>
                        setEditFolder({ id: folder.id, label: folder.label })
                      }
                    />
                    <Button
                      small
                      intent="primary"
                      text="Reactivate"
                      loading={
                        reactivateFolderMutation.isPending &&
                        reactivateFolderMutation.variables?.folderId ===
                          folder.id
                      }
                      disabled={reactivateFolderMutation.isPending}
                      onClick={() =>
                        reactivateFolderMutation.mutate({ folderId: folder.id })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
      >
        <Button
          text="Deactivated folders"
          icon="box"
          onClick={() => setIsOpen((open) => !open)}
        />
      </Popover>
      <FolderAdminEditFolderDialog
        folder={editFolder}
        onClose={() => setEditFolder(null)}
      />
    </>
  );
}

export default FolderAdminReactivatePopover;
